<template>
  <div
    class="awaiting h-full flex flex-col items-center justify-center relative overflow-hidden flex-1"
    :class="{'pb-30 px-6': !isMobileDevice, 'px-8': isMobileDevice}"
  >
    <!-- Ambient drifting gradient orbs -->
    <div class="absolute inset-0 pointer-events-none -z-10" aria-hidden="true">
      <div class="orb orb-a"></div>
      <div class="orb orb-b"></div>
    </div>

    <div class="relative w-full max-w-xl text-center" :class="isMobileDevice ? 'space-y-6' : 'space-y-10'">
      <!-- Animated drumhead emblem with sound ripples -->
      <div
        ref="emblemSection"
        class="emblem relative mx-auto"
        :class="isMobileDevice ? 'h-20 w-20' : 'h-32 w-32'"
      >
        <span class="ripple ripple-1"></span>
        <span class="ripple ripple-2"></span>
        <span class="ripple ripple-3"></span>
        <div class="emblem-core absolute inset-3 rounded-full bg-gradient-to-br from-primary via-primary to-text-tertiary shadow-2xl shadow-text-tertiary/30 flex items-center justify-center ring-1 ring-text-tertiary/20">
          <MusicalNoteIcon class="text-text-primary" :class="isMobileDevice ? 'w-6 h-6' : 'w-12 h-12'" />
        </div>
      </div>

      <!-- Heading block -->
      <div ref="awaitingHeading" :class="isMobileDevice ? 'space-y-2' : 'space-y-3'">
        <p class="font-semibold uppercase text-text-tertiary/70" :class="isMobileDevice ? 'text-[9px] tracking-[0.3em]' : 'text-xs tracking-[0.35em]'">
          Backstage Pass · Tuning Up
        </p>
        <h1
          class="font-bold leading-[1.05] text-text-primary"
          :class="isMobileDevice ? 'text-3xl' : 'text-6xl'"
        >
          Welcome to DrumbleBee
        </h1>
        <p
          class="mx-auto text-text-secondary leading-relaxed"
          :class="isMobileDevice ? 'text-sm max-w-sm' : 'text-lg max-w-md'"
        >
          You're in. The kit is tuned. We're just waiting on a role before you take the stage.
        </p>
      </div>

      <!-- Information card -->
      <div
        ref="awaitingCard"
        class="awaiting-card relative overflow-hidden rounded-2xl border border-primary/30 bg-surface/80 backdrop-blur-sm shadow-xl shadow-text-tertiary/10"
        :class="isMobileDevice ? 'p-4' : 'p-6'"
      >
        <div class="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/60 to-transparent"></div>

        <div class="flex items-start gap-4 text-left">
          <div class="shrink-0 grid place-items-center w-11 h-11 rounded-xl bg-primary/20 border border-primary/40 text-text-tertiary">
            <KeyIcon class="w-5 h-5" />
          </div>
          <div class="min-w-0 flex-1 space-y-1.5">
            <p class="font-semibold text-text-primary text-base">
              Awaiting role assignment
            </p>
            <p class="text-sm text-text-secondary leading-relaxed">
              Please ask the admin to assign permissions to your account, then refresh this page.
            </p>
            <div v-if="email" class="pt-3 mt-2 border-t border-primary/15 flex items-center gap-2 text-xs text-text-tertiary">
              <span class="status-dot inline-block w-1.5 h-1.5 rounded-full bg-primary"></span>
              <span class="font-mono truncate">{{ email }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDeviceDetection } from '@/composables/useDeviceDetection'
import { KeyIcon, MusicalNoteIcon } from '@heroicons/vue/24/outline'
import gsap from 'gsap'
import { onMounted, ref } from 'vue'

defineProps<{
  email?: string
}>()

const { isMobileDevice } = useDeviceDetection()

const emblemSection = ref<HTMLElement>()
const awaitingHeading = ref<HTMLElement>()
const awaitingCard = ref<HTMLElement>()

onMounted(() => {
  if (!emblemSection.value || !awaitingHeading.value || !awaitingCard.value) return

  gsap.set([emblemSection.value, awaitingHeading.value, awaitingCard.value], {
    opacity: 0,
    y: 30,
  })

  gsap.timeline({ delay: 0.15 })
    .to(emblemSection.value, {
      opacity: 1,
      y: 0,
      duration: 0.9,
      ease: 'power3.out',
    })
    .to(awaitingHeading.value, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
    }, '-=0.45')
    .to(awaitingCard.value, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      ease: 'power2.out',
    }, '-=0.4')
})
</script>

<style scoped>
/* Ambient gradient orbs that drift slowly behind the content */
.orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(72px);
  will-change: transform;
}
.orb-a {
  top: -12%;
  left: -10%;
  width: 28rem;
  height: 28rem;
  background: radial-gradient(circle, var(--color-primary) 0%, transparent 70%);
  opacity: 0.45;
  animation: drift-a 22s ease-in-out infinite;
}
.orb-b {
  bottom: -18%;
  right: -12%;
  width: 32rem;
  height: 32rem;
  background: radial-gradient(circle, var(--color-text-tertiary) 0%, transparent 70%);
  opacity: 0.28;
  animation: drift-b 28s ease-in-out infinite;
}
@keyframes drift-a {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(40px, 30px) scale(1.08); }
}
@keyframes drift-b {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%      { transform: translate(-32px, -24px) scale(0.94); }
}

/* Concentric sound-ripple rings around the drumhead emblem */
.emblem { isolation: isolate; }
.ripple {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  border: 1px solid var(--color-primary);
  opacity: 0;
  transform: scale(1);
  animation: ripple 2.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
  will-change: transform, opacity;
}
.ripple-1 { animation-delay: 0s; }
.ripple-2 { animation-delay: 0.8s; }
.ripple-3 { animation-delay: 1.6s; }
@keyframes ripple {
  0%   { opacity: 0.55; transform: scale(1); }
  80%  { opacity: 0;    transform: scale(1.85); }
  100% { opacity: 0;    transform: scale(1.85); }
}

/* Slow breathing for the emblem core */
.emblem-core {
  animation: breathe 3.6s ease-in-out infinite;
}
@keyframes breathe {
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.04); }
}

/* Soft pulse on the email status dot */
.status-dot {
  animation: dot-pulse 1.6s ease-in-out infinite;
  box-shadow: 0 0 0 0 var(--color-primary);
}
@keyframes dot-pulse {
  0%, 100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 60%, transparent); }
  50%      { box-shadow: 0 0 0 6px color-mix(in srgb, var(--color-primary) 0%, transparent); }
}
</style>
